import { Box, Typography } from "@mui/material";
import {
  ArchiveOutlined,
  Close,
  DashboardCustomizeOutlined,
  DeleteOutline,
  Rotate90DegreesCcw,
  Warning,
} from "@mui/icons-material";
import Icon from "@mdi/react";
import WarningIcon from "@mui/icons-material/Warning";
import { palettes } from "../../../common/themes/palletes";
import { NotificationType } from "../../../common/enums/notification-type";
import { NotificationModel } from "../../../common/components/notifications/api/types";
import { notificationItem } from "../sxprops";
import { ConfirmDeleteDialog } from "./delete-modal";
import useNotificationsStore from "../api/store";
import { useArchiveNotification, useIsReadNotification } from "../api/hooks";
import { mdiEyeCheckOutline, mdiEyeRemoveOutline } from "@mdi/js";

interface INotificationItem {
  notification: NotificationModel;
}

export const NotificationItem: React.FC<INotificationItem> = ({
  notification,
}) => {
  const notificationsStore = useNotificationsStore();

  const { mutate: archive } = useArchiveNotification();
  const { mutate: read } = useIsReadNotification();

  const handleArchive = () => {
    notificationsStore.setSelectedNotifId(notification.id);

    const created = new Date(notification.createdAt).getTime();
    const now = Date.now();
    const daysOld = (now - created) / (1000 * 60 * 60 * 24);

    if (daysOld > 60) {
      notificationsStore.setShowDeleteDialog(true);
    } else {
      archive();
    }
  };

  const handleRead = () => {
    notificationsStore.setSelectedNotifId(notification.id);
    read();
  };

  const handleDelete = () => {
    notificationsStore.setSelectedNotifId(notification.id);
    notificationsStore.setShowDeleteDialog(true);
  };

  const Title = () => {
    switch (notification.type) {
      case NotificationType.FacilityUserDeletion:
        return (
          <Typography variant="body1" fontWeight={600} color={"primary.main"}>
            Access Removed from{" "}
            <span className="bodyText" style={{ color: palettes.$danger }}>
              {notification.facilityName}
            </span>
          </Typography>
        );
      case NotificationType.FailedLoginAttempt:
        return (
          <Typography variant="body1" fontWeight={600} color={"primary.main"}>
            Suspicious Activity Detected{" "}
            <span className="bodyText" style={{ color: palettes.$danger }}>
              {notification.facilityName}
            </span>
          </Typography>
        );
      case NotificationType.NewSignIn:
        return (
          <Typography variant="body1" fontWeight={600} color={"primary.main"}>
            New Sign-in Alert
          </Typography>
        );
      case NotificationType.NewAppAccess:
        return (
          <Typography variant="body1" fontWeight={600} color={"primary.main"}>
            New Application Access Granted
          </Typography>
        );
      default:
        <>Title Unknown</>;
    }
  };

  const NotifIcon = () => {
    switch (notification.type) {
      case NotificationType.FacilityUserDeletion:
        return <Close sx={{ color: "error.main", fontSize: "inherit" }} />;
      case NotificationType.FailedLoginAttempt:
        return (
          <WarningIcon sx={{ color: "error.main", fontSize: "inherit" }} />
        );
      case NotificationType.NewSignIn:
        return (
          <Warning
            sx={{ color: palettes.$secondaryColor, fontSize: "inherit" }}
          />
        );
      case NotificationType.NewAppAccess:
        return (
          <DashboardCustomizeOutlined
            sx={{ color: "#123257", fontSize: "inherit" }}
          />
        );
      default:
        <>Icon Unknown</>;
    }
  };

  const Body = () => {
    switch (notification.type) {
      case NotificationType.FacilityUserDeletion:
        return (
          <Typography variant="caption" color={"text.secondary"}>
            <span
              className="bodyText"
              style={{ color: palettes.$secondaryColor }}
            >
              Reason:{" "}
            </span>
            {notification.reason}
          </Typography>
        );
      case NotificationType.NewSignIn:
        return (
          <>
            <Typography variant="caption" color={"text.secondary"}>
              Your account was accessed from{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.operatingSystem}
              </span>{" "}
              in{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.city === undefined ||
                notification.country === undefined
                  ? "Unknown Location"
                  : `${notification.city}, ${notification.country}`}
              </span>
              .{" "}
            </Typography>

            <Typography variant="caption" color={"text.secondary"}>
              If this was you, no action needed. If not, please secure your
              account.
            </Typography>
          </>
        );
      case NotificationType.NewAppAccess:
        return (
          <>
            <Typography variant="caption" color={"text.secondary"}>
              You have been granted access to{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.applicationName}
              </span>{" "}
              under the{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.groupName}
              </span>{" "}
              group
              {notification.facilityName && (
                <>
                  {" "}
                  at{" "}
                  <span
                    className="bodyText"
                    style={{ color: palettes.$secondaryColor }}
                  >
                    {notification.facilityName}
                  </span>
                </>
              )}
              .{" "}
            </Typography>

            <Typography variant="caption" color={"text.secondary"}>
              If you did not request this access, please contact support
              immediately.
            </Typography>
          </>
        );
      case NotificationType.FailedLoginAttempt:
        return (
          <>
            <Typography variant="caption" color={"text.secondary"}>
              We detected multiple invalid login attempts on your account from{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.operatingSystem === undefined
                  ? "Unknown Device"
                  : notification.operatingSystem}
              </span>{" "}
              in{" "}
              <span
                className="bodyText"
                style={{ color: palettes.$secondaryColor }}
              >
                {notification.city === undefined
                  ? "Unknown Location. "
                  : `${notification.city}. `}
              </span>
            </Typography>

            <Typography variant="caption" color={"text.secondary"}>
              If this was not you, we recommend securing your account
              immediately.
            </Typography>
          </>
        );
      default:
        <>Body Unknown</>;
    }
  };

  return (
    <Box
      sx={{
        ...notificationItem.mainCont,
        backgroundColor: "#F5FAFF",
        borderRadius: 2,
        p: 2,
        "&:hover .readIndicator": {
          backgroundColor: notification.isRead ? "transparent" : "#123257",
        },
        "&:hover .bodyText": {
          color: "#fff !important",
        },
      }}
    >
      <Box sx={notificationItem.mainCont.wrapper}>
        <Box sx={notificationItem.mainCont.wrapper.iconCont}>{NotifIcon()}</Box>
        <Box sx={notificationItem.mainCont.wrapper.mainCont}>
          <Box sx={{ mb: 1 }}>{Title()}</Box>
          <Box sx={{ mb: 1 }}>{Body()}</Box>
        </Box>

        <Box sx={{}}>
          <Box sx={notificationItem.mainCont.wrapper.actionsGroup}>
            <Box
              className="actionIcons"
              sx={notificationItem.mainCont.wrapper.actionIcons}
            >
              <Box
                id="notification-item-archive-button"
                className="downloadWrapper"
                onClick={handleArchive}
                sx={{ cursor: "pointer" }}
              >
                {notificationsStore.tab === "archive" ? (
                  <Rotate90DegreesCcw fontSize="small" />
                ) : (
                  <ArchiveOutlined fontSize="small" />
                )}
              </Box>
              <Box id="notification-item-delete-button" className="deleteIcon" onClick={handleDelete}>
                <DeleteOutline fontSize="small" />
              </Box>
              <Box id="notification-item-read-button" className="readIcon" onClick={handleRead}>
                {notification.isRead ? (
                  <Icon path={mdiEyeRemoveOutline} size={1} />
                ) : (
                  <Icon path={mdiEyeCheckOutline} size={1} />
                )}
              </Box>
            </Box>
            <Box
              className="readIndicator"
              sx={{
                bgcolor: notification.isRead ? "transparent" : "secondary.main",
                ...notificationItem.mainCont.wrapper.readIndicator,
              }}
            ></Box>
          </Box>
        </Box>
      </Box>

      <Box gap={2} sx={notificationItem.mainCont.infoWrapper}>
        <Typography variant="caption">
          {new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(new Date(notification.createdAt))}
        </Typography>
        <Typography variant="caption">
          {`${new Date(notification.createdAt).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })}`}
        </Typography>
      </Box>
      <ConfirmDeleteDialog />
    </Box>
  );
};
